import React, { useRef, useState } from 'react';
import { useInView, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Check, RotateCcw } from 'lucide-react'; 

// Import your images here
import PhoneImage from "../../Components/assets/gifphone.gif";
import Banner1 from "../assets/card1.jpg"
import Banner2 from "../assets/card2.jpg"
import Banner3 from "../assets/card3.jpg"

/* ---------------- ANIMATIONS ---------------- */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, ease: "easeOut" }
  }
};

const slideLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const slideRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const AnimatedSection = ({ children, variants, className }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ---------------- CARD COMPONENT ---------------- */
const FeatureCard = ({ data }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const hasContent = data.title && data.description;

  return (
    /* Card Container - Keeping original dimensions */
    <div className="relative w-full h-[350px] md:h-[320px] [perspective:1500px]">
      <motion.div
        className="relative w-full h-full [transform-style:preserve-3d]"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        /* Clean & Slow Professional Single Flip */
        transition={{ 
          duration: 0.8, 
          ease: [0.4, 0, 0.2, 1] // Professional cubic-bezier for smooth motion
        }}
      >
        
        {/* --- FRONT SIDE (Original Design) --- */}
        <div 
          className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden shadow-xl bg-white border border-slate-100 [backface-visibility:hidden]"
        >
          <img 
            src={data.image} 
            alt={data.title || "Feature Banner"}
            className="absolute inset-0 w-full h-full object-cover" 
          />
          
          {hasContent && (
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C4550] via-[#0C4550]/95 to-transparent flex flex-col justify-center p-8 md:p-10 w-[100%] md:w-[85%] lg:w-[75%]">
               <motion.div
                 initial={{ opacity: 0, x: -20 }}
                 whileInView={{ opacity: 1, x: 0 }}
                 viewport={{ once: true }}
                 transition={{ delay: 0.2 }}
               >
                 <h3 className="text-3xl font-medium text-white mb-4 flex items-center gap-3">
                   {data.title}
                   <span className="h-2 w-2 rounded-full bg-[#F46653] animate-pulse"></span>
                 </h3>
                 
                 <p className="text-white text-sm md:text-base leading-relaxed opacity-90 max-w-sm font-medium">
                   {data.description}
                 </p>
                 
                 <div 
                    onClick={() => setIsFlipped(true)}
                    className="mt-6 flex items-center text-[#F46653] font-semibold text-sm tracking-wide uppercase cursor-pointer hover:text-white transition-colors"
                  >
                    Learn more <ArrowUpRight className="w-4 h-4 ml-1" />
                 </div>
               </motion.div>
            </div>
          )}
          <div className="absolute inset-0 border border-black/5 rounded-2xl pointer-events-none" />
        </div>

        {/* --- BACK SIDE (Professional Detail View) --- */}
        <div 
          className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden shadow-2xl bg-white border-2 border-[#0C4550] [backface-visibility:hidden] [transform:rotateY(180deg)] p-8 flex flex-col"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-xl font-bold text-[#0C4550]">{data.title} Details</h4>
            <button 
              onClick={() => setIsFlipped(false)}
              className="p-2 bg-slate-50 rounded-full hover:bg-slate-200 transition-colors"
            >
              <RotateCcw className="w-5 h-5 text-[#F46653]" />
            </button>
          </div>

          <div className="flex-grow space-y-4">
            {data.bullets.map((point, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-1 bg-[#0C4550] rounded-full p-0.5 flex-shrink-0">
                    <Check className="w-3 h-3 text-white" />
                </div>
                <p className="text-slate-700 text-sm md:text-base font-semibold leading-tight">{point}</p>
              </div>
            ))}
          </div>

          <button 
            onClick={() => setIsFlipped(false)}
            className="mt-4 w-full py-3 bg-[#0C4550] text-white rounded-xl font-bold text-sm tracking-widest uppercase hover:opacity-90 transition-all shadow-md"
          >
            Go Back
          </button>
        </div>

      </motion.div>
    </div>
  );
};

/* ---------------- MAIN CONTENT ---------------- */
const Content = () => {
  const navigate = useNavigate();

  const features = [
    { 
      id: 1, 
      image: Banner1,
      title: "Bulk SMS",
      description: "Send high-volume SMS campaigns instantly with DLT registered sender IDs and pre-approved templates. Perfect for marketing campaigns, alerts, and mass notifications.",
      bullets: [
        "DLT registered sender ID management",
        "High-volume throughput (5000+ SMS/sec)",
        "Pre-approved template integration",
        "Instant delivery with retry logic"
      ]
    },
    { 
      id: 4, 
      image: "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=2032&auto=format&fit=crop",
      title: "Enterprise API Gateway",
      description: "Scale effortlessly with our developer-first API architecture. Experience 99.99% uptime, intelligent routing, and banking-grade security for your critical communications.",
      bullets: [
        "99.99% Guaranteed uptime SLA",
        "Intelligent routing engine",
        "Banking-grade security (AES-256)",
        "Seamless API integration architecture"
      ]
    },
    { 
      id: 3, 
      image: Banner3,
      title: "Real-Time Analytics",
      description: "Gain deep insights into your campaigns with our advanced dashboard. Track delivery rates, monitor open rates, and download detailed reports to optimize your ROI instantly.",
      bullets: [
        "Interactive dashboard interface",
        "Live delivery and open rate tracking",
        "Deep-dive downloadable reports",
        "Instant ROI optimization insights"
      ]
    }, 
    { 
      id: 2, 
      image: Banner2,
      title: "Channel Leadership",
      description: "We bring to life omnichannel communications strategies with significant shares across key channels like SMS, WhatsApp, LMS, and IVR Service.",
      bullets: [
        "Official WhatsApp Business API",
        "Interactive IVR & Voice services",
        "Omni-channel strategy execution",
        "Multi-channel message orchestration"
      ]
    },
  ];

  return (
    <section className="bg-slate-50 py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-[1600px] mx-auto">
        <AnimatedSection variants={containerVariants} className="text-center mb-16">
          <motion.p className="text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 mb-6">
            Powerful SMS Solutions
          </motion.p>
          <motion.p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Reliable, scalable, and enterprise-grade messaging services to help your business connect, convert, and grow effortlessly.
          </motion.p>
        </AnimatedSection>

        <AnimatedSection variants={containerVariants}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 place-items-center">
            {features.map((feature, index) => (
              <motion.div key={feature.id} variants={index % 2 === 0 ? slideLeft : slideRight} className="w-full">
                <FeatureCard data={feature} />
              </motion.div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection variants={containerVariants} className="mt-20 w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 bg-[#0C4550] rounded-2xl shadow-lg p-8 max-w-5xl mx-auto gap-6">
            <AnimatedSection variants={slideLeft}>
              <div className="flex flex-col justify-center h-full min-h-[300px] text-center">
                <p className="text-[42px] font-extrabold tracking-tight text-white mb-3">Ready to Get Started?</p>
                <p className="text-gray-200 mb-6 text-lg leading-relaxed">Join thousands of businesses that trust our secure platform.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.button onClick={() => navigate('/login')} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="bg-white text-[#012B54] font-semibold text-[20px] py-3 px-6 rounded-xl shadow-md">Join Us</motion.button>
                  <motion.button onClick={() => navigate('/contact')} whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.08)" }} whileTap={{ scale: 0.95 }} className="border border-white/40 text-white font-semibold text-[20px] py-3 px-6 rounded-xl">Contact Us</motion.button>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection variants={slideRight}>
              <div className="flex justify-center items-center h-full">
                <motion.img src={PhoneImage} alt="Business illustration" className="w-full max-w-sm rounded-xl" whileHover={{ scale: 1.03 }} />
              </div>
            </AnimatedSection>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Content;